import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Company activation status — driven by Stripe subscription state.
 * - Super admins are always activated
 * - Company users with subscription.status IN (trialing, active, past_due) are activated
 * - Everyone else is UN-activated → app is gated to Settings → Billing only
 */
const ActivationContext = createContext({ activated: true, loading: true, refresh: () => {} });

export function ActivationProvider({ children }) {
  const { user, checking } = useAuth();
  const [state, setState] = useState({ activated: true, loading: true, reason: "", status: "none", plan_name: null, trial_end: null, company_name: "" });

  const refresh = useCallback(async () => {
    // Wait until AuthContext has finished checking — otherwise we can flash
    // activated=false during the first render and cause a spurious redirect.
    if (checking) return;
    if (!user) { setState({ activated: false, loading: false, reason: "no_user" }); return; }
    try {
      const { data } = await api.get("/billing/activation-status");
      setState({ ...data, loading: false });
    } catch (e) {
      // Fail open — don't lock the user out on transient network hiccups
      setState({ activated: true, loading: false, reason: "error" });
    }
  }, [user, checking]);

  useEffect(() => { refresh(); }, [refresh]);
  // Re-check when the user navigates back to a Settings sub-route (e.g., after Stripe return)
  useEffect(() => {
    const on = () => refresh();
    window.addEventListener("focus", on);
    return () => window.removeEventListener("focus", on);
  }, [refresh]);

  return (
    <ActivationContext.Provider value={{ ...state, refresh }}>
      {children}
    </ActivationContext.Provider>
  );
}

export const useActivation = () => useContext(ActivationContext);
